import type { Component, MaybeRefOrGetter } from "vue";
import type { DialogSize } from "../../components/Dialog/dialogSizes";
import type { IconName } from "../../components/Icon/icons";
import { markRaw, reactive, toValue, watch } from "vue";
import ConfirmDialogBody from "../../components/Dialog/ConfirmDialogBody.vue";
import { getDialogStack } from "../../components/Dialog/dialogStack";

export interface ConfirmOptions {
  title: MaybeRefOrGetter<string>
  message?: MaybeRefOrGetter<string>
  variant?: MaybeRefOrGetter<"danger" | "warning" | "primary" | "info" | "success" | "secondary">
  confirmText?: MaybeRefOrGetter<string>
  cancelText?: MaybeRefOrGetter<string>
  icon?: MaybeRefOrGetter<IconName>
  body?: Component
  bodyProps?: Record<string, unknown>
  onConfirm?: () => Promise<void>
  confirmDisabled?: MaybeRefOrGetter<boolean>
  hideCancel?: boolean
  /** Can follow state, e.g. a confirm that turns into a notice once a check shows the action cannot run. */
  hideConfirm?: MaybeRefOrGetter<boolean>
  size?: DialogSize
  /** Exact width in px instead of the width of `size`, e.g. 440 for a board that needs it. */
  width?: number
  id?: string
}

interface ConfirmLayerProps {
  confirmLoading: boolean
}

interface ConfirmLayerRequest<TProps extends ConfirmLayerProps> {
  component: Component
  size: DialogSize
  width?: number
  id?: string
  /** The body draws its own header, content and footer edge to edge (with their own padding and dividers). */
  paddingless?: boolean
  /** The body's props; `confirmLoading` is driven while `onConfirm` runs. */
  props: TProps
  /** Re-reads the reactive options, so a title or a disabled state can change while the confirm is open. */
  sync: () => Partial<TProps>
  onConfirm?: () => Promise<void>
}

/**
 * Pushes a headless confirm layer whose body emits `confirm` and `cancel`, and resolves with the choice. With
 * `onConfirm` the layer stays open while it runs, and stays open when it throws so the user can retry or cancel.
 */
export function openConfirmLayer<TProps extends ConfirmLayerProps>(request: ConfirmLayerRequest<TProps>): Promise<boolean> {
  const stack = getDialogStack();

  return new Promise((resolve) => {
    let settled = false;
    let stopWatch: (() => void) | undefined;
    const props = reactive(request.props) as TProps;

    function finish(value: boolean) {
      if (settled) {
        return;
      }
      settled = true;
      stopWatch?.();
      resolve(value);
    }

    stopWatch = watch(request.sync, (next) => {
      Object.assign(props, next);
    });

    const instanceId = stack.push({
      id: request.id ?? `confirm-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      component: request.component,
      size: request.size,
      width: request.width,
      paddingless: request.paddingless ?? false,
      alert: true,
      mask: true,
      dismissible: true,
      headless: true,
      props: props as unknown as Record<string, unknown>,
      onClose: () => finish(false),
    });

    const layer = stack.get(instanceId);
    if (!layer) {
      finish(false);
      return;
    }

    layer.listeners = {
      confirm: () => {
        void (async () => {
          if (request.onConfirm) {
            props.confirmLoading = true;
            try {
              await request.onConfirm();
              finish(true);
              void stack.pop(instanceId);
            } catch {
              // keep the layer open so the caller can retry or cancel
            } finally {
              props.confirmLoading = false;
            }
            return;
          }
          finish(true);
          void stack.pop(instanceId);
        })();
      },
      cancel: () => {
        finish(false);
        void stack.pop(instanceId);
      },
    };
  });
}

export function confirm(options: ConfirmOptions): Promise<boolean> {
  function readOptions() {
    return {
      title: toValue(options.title),
      message: toValue(options.message) ?? "",
      confirmText: toValue(options.confirmText) ?? "Confirm",
      cancelText: toValue(options.cancelText) ?? "Cancel",
      type: toValue(options.variant) ?? "primary",
      icon: toValue(options.icon) ?? "",
      confirmDisabled: toValue(options.confirmDisabled) ?? false,
      hideConfirm: toValue(options.hideConfirm) ?? false,
    };
  }

  return openConfirmLayer({
    component: ConfirmDialogBody,
    size: options.size ?? "confirm",
    width: options.width,
    id: options.id,
    onConfirm: options.onConfirm,
    props: {
      ...readOptions(),
      body: options.body ? markRaw(options.body) : undefined,
      bodyProps: options.bodyProps,
      confirmLoading: false,
      hideCancel: options.hideCancel ?? false,
    },
    sync: readOptions,
  });
}
